import { getAiTelemetryContext, track } from "../analytics";

type AiFeature = "component" | "system" | "system-edit" | "assistant";
type Outcome = "success" | "denied" | "failed";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function telemetryContext() {
  try { return getAiTelemetryContext(); }
  catch { return { enabled: false, distinctId: null, sessionId: null }; }
}

/** The request's existing random ID also joins recovery polls to one AI trace.
 * This helper accepts only IDs and fixed outcomes, never prompts or results. */
export function createAiRequestTelemetry(feature: AiFeature, requestId: string) {
  const traceId = UUID.test(requestId) ? requestId : crypto.randomUUID();
  const context = telemetryContext();
  const startedAt = Date.now();
  let completed = false;
  return {
    get headers(): Record<string, string> {
      const current = telemetryContext();
      const headers: Record<string, string> = { "X-Crimp-AI-Trace-Id": traceId };
      if (!context.enabled || !current.enabled || current.distinctId !== context.distinctId) {
        headers["X-Crimp-Analytics-Disabled"] = "1";
      } else {
        if (context.distinctId && UUID.test(context.distinctId)) headers["X-PostHog-Distinct-Id"] = context.distinctId;
        if (context.sessionId && UUID.test(context.sessionId)) headers["X-PostHog-Session-Id"] = context.sessionId;
      }
      return headers;
    },
    finish(outcome: Outcome, status: number | null): void {
      if (completed) return;
      completed = true;
      try {
        const current = telemetryContext();
        // A pending request must not become an event on the next account.
        if (!context.enabled || !current.enabled || current.distinctId !== context.distinctId) return;
        track("ai_request_completed", {
          $ai_trace_id: traceId,
          feature,
          outcome,
          status: typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599 ? status : null,
          duration_ms: Math.max(0, Date.now() - startedAt),
        });
      } catch { /* Analytics must not change the generation or recovery result. */ }
    },
  };
}
